import { Map, List, fromJS } from "immutable";
import { confirmAlert } from "react-confirm-alert";
import { Vertex as VertexModel } from "../models";

import { Group, Layer, Hole, Vertex, Area, Project } from "./export";
import {
  IDBroker,
  NameGenerator,
  GeometryUtils,
  SnapUtils,
  SnapSceneUtils,
} from "../utils/export";
import { fraLog, fraSplitSnapshot } from "../utils/fra-debug";
import { fraRemoteLog } from "../utils/fra-remote-log";
import {
  getLineOrientation as wallOrient,
  wouldCoincideWithParallelWalls,
  straightenNearOrthoEndpoints,
} from "../utils/wall-safety";
import {
  MODE_IDLE,
  MODE_WAITING_DRAWING_LINE,
  MODE_DRAWING_LINE,
  MODE_DRAGGING_LINE,
  MODE_DRAGGING_SQUARE,
  MODE_WAITING_DRAWING_SQUARE,
  UPDATE_DRAWING_SQUARE,
  MODE_DRAWING_SQUARE,
} from "../constants";

/** Count angled (non-ortho) walls — remote logs detect bay angles becoming straight. */
function countDiagonalWalls(state, layerID) {
  let n = 0;
  const layer = state.getIn(["scene", "layers", layerID]);
  if (!layer) return 0;
  layer.get("lines").forEach((line) => {
    const v0 = layer.getIn(["vertices", line.vertices.get(0)]);
    const v1 = layer.getIn(["vertices", line.vertices.get(1)]);
    if (!v0 || !v1) return;
    const dx = Math.abs(v0.x - v1.x);
    const dy = Math.abs(v0.y - v1.y);
    if (dx > 2 && dy > 2) n += 1;
  });
  return n;
}

function layerStats(state, layerID) {
  const layer = state.getIn(["scene", "layers", layerID]);
  if (!layer) return { lines: 0, areas: 0, diagonals: 0 };
  return {
    lines: layer.get("lines").size,
    areas: layer.get("areas").size,
    diagonals: countDiagonalWalls(state, layerID),
  };
}

class Line {
  static create(state, layerID, type, x0, y0, x1, y1, properties) {
    let lineID = IDBroker.acquireID();

    let { updatedState: stateV0, vertex: v0 } = Vertex.add(
      state,
      layerID,
      x0,
      y0,
      "lines",
      lineID
    );
    let { updatedState: stateV1, vertex: v1 } = Vertex.add(
      stateV0,
      layerID,
      x1,
      y1,
      "lines",
      lineID
    );
    state = stateV1;

    let line = state.catalog.factoryElement(
      type,
      {
        id: lineID,
        name: NameGenerator.generateName(
          "lines",
          state.catalog.getIn(["elements", type, "info", "title"])
        ),
        vertices: new List([v0.id, v1.id]),
        type,
      },
      properties
    );

    state = state.setIn(["scene", "layers", layerID, "lines", lineID], line);

    return { updatedState: state, line };
  }

  // Stub anchored at an existing junction; free end moves with the dragged wall.
  static createProtrusionStub(
    state,
    layerID,
    type,
    startVertexId,
    endX,
    endY,
    properties,
    orientation
  ) {
    const startVertex = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      startVertexId,
    ]);
    if (!startVertex) return { updatedState: state, line: null };

    let x0 = startVertex.x;
    let y0 = startVertex.y;
    let x1 = x0;
    let y1 = y0;
    if (orientation === "vertical") x1 = endX;
    else if (orientation === "horizontal") y1 = endY;
    else {
      x1 = endX;
      y1 = endY;
    }

    if (GeometryUtils.samePoints({ x: x0, y: y0 }, { x: x1, y: y1 })) {
      return { updatedState: state, line: null };
    }

    const lineID = IDBroker.acquireID();
    const endVertex = new VertexModel({
      id: IDBroker.acquireID(),
      name: "Vertex",
      x: x1,
      y: y1,
      lines: new List([lineID]),
      areas: new List(),
    });

    state = Vertex.addElement(
      state,
      layerID,
      startVertexId,
      "lines",
      lineID
    ).updatedState;
    state = state.setIn(
      ["scene", "layers", layerID, "vertices", endVertex.id],
      endVertex
    );

    const line = state.catalog.factoryElement(
      type,
      {
        id: lineID,
        name: NameGenerator.generateName(
          "lines",
          state.catalog.getIn(["elements", type, "info", "title"])
        ),
        vertices: new List([startVertexId, endVertex.id]),
        type,
      },
      properties
    );

    state = state.setIn(["scene", "layers", layerID, "lines", lineID], line);

    return {
      updatedState: state,
      line,
      startVertexId,
      endVertexId: endVertex.id,
    };
  }

  static select(state, layerID, lineID) {
    state = Layer.select(state, layerID).updatedState;

    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    // Wall may have been removed/merged during split finalize — never crash.
    if (!line) {
      return { updatedState: state };
    }

    state = Layer.selectElement(state, layerID, "lines", lineID).updatedState;
    state = Layer.selectElement(
      state,
      layerID,
      "vertices",
      line.vertices.get(0)
    ).updatedState;
    state = Layer.selectElement(
      state,
      layerID,
      "vertices",
      line.vertices.get(1)
    ).updatedState;

    return { updatedState: state };
  }

  static remove(state, layerID, lineID) {
    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);

    if (line) {
      state = this.unselect(state, layerID, lineID).updatedState;
      line.holes.forEach(
        (holeID) => (state = Hole.remove(state, layerID, holeID).updatedState)
      );
      state = Layer.removeElement(state, layerID, "lines", lineID).updatedState;

      line.vertices.forEach(
        (vertexID) => {
          if (
            state.hasIn([
              "scene",
              "layers",
              layerID,
              "vertices",
              vertexID,
            ])
          ) {
            state = Vertex.remove(
              state,
              layerID,
              vertexID,
              "lines",
              lineID
            ).updatedState;
          }
        }
      );

      state
        .getIn(["scene", "groups"])
        .forEach(
          (group) =>
            (state = Group.removeElement(
              state,
              group.id,
              layerID,
              "lines",
              lineID
            ).updatedState)
        );
    }

    return { updatedState: state };
  }

  static unselect(state, layerID, lineID) {
    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);

    if (line) {
      state = Layer.unselect(
        state,
        layerID,
        "vertices",
        line.vertices.get(0)
      ).updatedState;
      state = Layer.unselect(
        state,
        layerID,
        "vertices",
        line.vertices.get(1)
      ).updatedState;
      state = Layer.unselect(state, layerID, "lines", lineID).updatedState;
    }

    return { updatedState: state };
  }

  static split(state, layerID, lineID, x, y, options = {}) {
    // skipSnap: weld/T-junction splits must use exact coords — snap causes
    // endless re-split cascades (hundreds of walls) and half-transparent rooms.
    const skipSnap = !!(options && options.skipSnap);
    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    if (!line) {
      return { updatedState: state, lines: new List() };
    }
    let v0 = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      line.vertices.get(0),
    ]);
    let v1 = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      line.vertices.get(1),
    ]);
    if (!v0 || !v1) {
      return { updatedState: state, lines: new List() };
    }
    let { x: x0, y: y0 } = v0;
    let { x: x1, y: y1 } = v1;

    if (!skipSnap) {
      let snapElements = SnapSceneUtils.sceneSnapElements(
        state.scene,
        new List(),
        state.snapMask
      );
      let snap = null;

      if (state.snapMask && !state.snapMask.isEmpty()) {
        snap = SnapUtils.nearestSnap(snapElements, x, y, state.snapMask);
        if (snap) ({ x, y } = snap.point);
      }
    }

    // Project click onto this wall so split works even when not exactly on the line.
    let projected = GeometryUtils.projectPointOntoLineSegment(
      x0,
      y0,
      x1,
      y1,
      x,
      y
    );
    x = projected.x;
    y = projected.y;

    // Refuse split too close to endpoints — creates tiny stubs that vanish / hide.
    const wallLen = GeometryUtils.pointsDistance(x0, y0, x1, y1);
    const d0 = GeometryUtils.pointsDistance(x, y, x0, y0);
    const d1 = GeometryUtils.pointsDistance(x, y, x1, y1);
    const minEnd = Math.max(40, wallLen * 0.08);
    if (wallLen < 80 || d0 < minEnd || d1 < minEnd) {
      return { updatedState: state, lines: new List() };
    }

    if (!skipSnap) {
      const wallSnap = Layer.snapPointToNearestWall(state, layerID, x, y);
      if (
        GeometryUtils.isPointOnLineSegment(
          x0,
          y0,
          x1,
          y1,
          wallSnap.x,
          wallSnap.y,
          15
        )
      ) {
        x = wallSnap.x;
        y = wallSnap.y;
        projected = GeometryUtils.projectPointOntoLineSegment(
          x0,
          y0,
          x1,
          y1,
          x,
          y
        );
        x = projected.x;
        y = projected.y;
      }
    }

    const minSplitDist = 20;
    const lineLength = GeometryUtils.pointsDistance(x0, y0, x1, y1);
    const distFromV0 = lineLength * projected.t;
    const distFromV1 = lineLength * (1 - projected.t);
    const atExistingJunction =
      distFromV0 <= 8 ||
      distFromV1 <= 8 ||
      state
        .getIn(["scene", "layers", layerID, "vertices"])
        .some(
          (vertex) =>
            GeometryUtils.samePoints(vertex, { x, y }) &&
            vertex.lines &&
            vertex.lines.size > 0
        );

    if (
      lineLength < minSplitDist * 2 ||
      (!atExistingJunction &&
        (projected.t <= 0.03 ||
          projected.t >= 0.97 ||
          distFromV0 < minSplitDist ||
          distFromV1 < minSplitDist))
    ) {
      return { updatedState: state, lines: new List([line]) };
    }

    let { updatedState: stateL1, line: line0 } = Line.create(
      state,
      layerID,
      line.type,
      x0,
      y0,
      x,
      y,
      line.get("properties")
    );
    let { updatedState: stateL2, line: line1 } = Line.create(
      stateL1,
      layerID,
      line.type,
      x1,
      y1,
      x,
      y,
      line.get("properties")
    );
    state = stateL2;
    // Straighten split pieces if they are near H/V (avoids 2px translate skew).
    state = this.straightenLineIfNearOrtho(state, layerID, line0.id, 12);
    state = this.straightenLineIfNearOrtho(state, layerID, line1.id, 12);
    const verticesLine0 = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      line0.id,
      "vertices",
    ]) || line0.get("vertices");
    const verticesLine1 = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      line1.id,
      "vertices",
    ]) || line1.get("vertices");
    let shared = verticesLine0.filter((vertex) =>
      verticesLine1.includes(vertex)
    );

    // Only track user/manual splits — weld/T-junction splits must not inflate
    // splittedVertices (that flooded logs and confused drag-end detect).
    if (!skipSnap) {
      state = state.update("splittedVertices", (sharedVertices) =>
        sharedVertices.concat(shared)
      );
    }
    let splitPointOffset = GeometryUtils.pointPositionOnLineSegment(
      x0,
      y0,
      x1,
      y1,
      x,
      y
    );
    let minVertex = GeometryUtils.minVertex(v0, v1);

    line.holes.forEach((holeID) => {
      let hole = state.getIn(["scene", "layers", layerID, "holes", holeID]);

      let holeOffset = hole.offset;
      if (minVertex.x === x1 && minVertex.y === y1) {
        splitPointOffset = 1 - splitPointOffset;
        holeOffset = 1 - hole.offset;
      }

      if (holeOffset < splitPointOffset) {
        let offset = holeOffset / splitPointOffset;
        if (minVertex.x === x1 && minVertex.y === y1) {
          offset = 1 - offset;
        }
        state = Hole.create(
          state,
          layerID,
          hole.type,
          line0.id,
          offset,
          hole.properties
        ).updatedState;
      } else {
        let offset = (holeOffset - splitPointOffset) / (1 - splitPointOffset);
        if (minVertex.x === x1 && minVertex.y === y1) {
          offset = 1 - offset;
        }
        state = Hole.create(
          state,
          layerID,
          hole.type,
          line1.id,
          offset,
          hole.properties
        ).updatedState;
      }
    });

    //add splitted lines to the original line's group
    let lineGroups = state.getIn(["scene", "groups"]).filter((group) => {
      const lines = group.getIn(["elements", layerID, "lines"]);
      return lines && lines.contains(lineID);
    });

    lineGroups.forEach((group) => {
      state = Group.addElement(
        state,
        group.id,
        layerID,
        "lines",
        line0.id
      ).updatedState;
      state = Group.addElement(
        state,
        group.id,
        layerID,
        "lines",
        line1.id
      ).updatedState;
    });

    state = Line.remove(state, layerID, lineID).updatedState;

    fraSplitSnapshot(state, layerID, "split complete", {
      originalLineId: lineID,
      newLineIds: [line0.id, line1.id],
      splitPoint: { x, y },
      junctionVertexIds: shared.toArray ? shared.toArray() : shared,
    });

    return { updatedState: state, lines: new List([line0, line1]) };
  }

  static isSplitMiddleAnchor(state, layerID, anchorID, draggedLineID, dragOrient) {
    const anchor = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      anchorID,
    ]);
    // size 2 = collinear split junction (A–B–C); size 3+ = T after extrusion.
    // Room L-corners are size 2 but perpendicular → parallelCount 0 → false.
    if (!anchor || anchor.lines.size < 2) return false;

    let parallelCount = 0;
    anchor.lines.forEach((connectedLineID) => {
      if (connectedLineID === draggedLineID) return;
      const connectedLine = state.getIn([
        "scene",
        "layers",
        layerID,
        "lines",
        connectedLineID,
      ]);
      if (!connectedLine || connectedLine.getIn(["misc", "_splitBridge"])) {
        return;
      }
      const cv0 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        connectedLine.vertices.get(0),
      ]);
      const cv1 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        connectedLine.vertices.get(1),
      ]);
      if (!cv0 || !cv1) return;
      if (this.getLineOrientation(cv0.x, cv0.y, cv1.x, cv1.y) === dragOrient) {
        parallelCount++;
      }
    });

    return parallelCount >= 1;
  }

  static finalizeSplitDrag(state, layerID, draggedLineID, draggingSupport) {
    let updatedState = state;
    const draggedLine = updatedState.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      draggedLineID,
    ]);
    if (!draggedLine || !draggingSupport) return { updatedState };

    const v0 = updatedState.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      draggedLine.vertices.get(0),
    ]);
    const v1 = updatedState.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      draggedLine.vertices.get(1),
    ]);
    if (!v0 || !v1) return { updatedState };

    // Prefer locked drag axis from beginDraggingLine (not live verts).
    const orient =
      (draggingSupport && draggingSupport.get("lineOrientation")) ||
      this.getLineOrientation(v0.x, v0.y, v1.x, v1.y);
    const bridgesToRemove = [];
    const openingAnchors = [];

    updatedState
      .getIn(["scene", "layers", layerID, "lines"])
      .forEach((line, bridgeID) => {
        if (!line.getIn(["misc", "_splitBridge"])) return;

        const touchesDragged = draggedLine.vertices.some((vid) =>
          line.vertices.includes(vid)
        );
        if (!touchesDragged) return;

        const aId = line.vertices.get(0);
        const bId = line.vertices.get(1);
        const a = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          aId,
        ]);
        const b = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          bId,
        ]);
        if (!a || !b) return;

        if (GeometryUtils.verticesDistance(a, b) < 3) {
          bridgesToRemove.push(bridgeID);
          return;
        }

        // Clone = endpoint on the dragged wall; anchor = fixed end.
        // NEVER move the anchor (room corner) — that shears connected walls
        // into diagonals (the angle bug). Degree-size heuristic was wrong when
        // both ends had size 2 and vertex order put the corner as "clone".
        const aOnDragged = draggedLine.vertices.includes(aId);
        const bOnDragged = draggedLine.vertices.includes(bId);
        let anchorID = null;
        let cloneID = null;
        if (aOnDragged && !bOnDragged) {
          cloneID = aId;
          anchorID = bId;
        } else if (bOnDragged && !aOnDragged) {
          cloneID = bId;
          anchorID = aId;
        } else {
          // Does not cleanly attach to dragged wall — promote only.
          updatedState = updatedState.setIn(
            ["scene", "layers", layerID, "lines", bridgeID, "misc"],
            new Map()
          );
          return;
        }

        const anchor = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          anchorID,
        ]);
        const clone = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          cloneID,
        ]);
        if (!anchor || !clone) return;

        // Ortho bridge: only nudge the dragged-wall endpoint on the cross axis.
        const straighten = { x: clone.x, y: clone.y };
        if (orient === "vertical") straighten.y = anchor.y;
        else if (orient === "horizontal") straighten.x = anchor.x;
        if (!GeometryUtils.samePoints(clone, straighten)) {
          updatedState = Vertex.setAttributes(
            updatedState,
            layerID,
            cloneID,
            new Map(straighten)
          ).updatedState;
        }
        updatedState = updatedState.setIn(
          ["scene", "layers", layerID, "lines", bridgeID, "misc"],
          new Map()
        );
        openingAnchors.push(anchorID);
      });

    bridgesToRemove.forEach((bridgeID) => {
      updatedState = Line.remove(updatedState, layerID, bridgeID).updatedState;
    });

    // Extra wall at bump-out mouth: leftover segment(s) still sitting between
    // the two fixed anchors on the old wall line. Remove only those, and only
    // after a real extrusion (tiny moves must not wipe the room side).
    const extruded =
      orient === "vertical"
        ? Math.abs(v0.x - (draggingSupport.get("startVertex0X") || v0.x))
        : orient === "horizontal"
          ? Math.abs(v0.y - (draggingSupport.get("startVertex0Y") || v0.y))
          : 0;
    if (openingAnchors.length === 2 && extruded > 25) {
      updatedState = this.removeSplitOpeningWall(
        updatedState,
        layerID,
        draggedLineID,
        openingAnchors[0],
        openingAnchors[1],
        orient
      );
    }

    const oldAxis =
      orient === "vertical"
        ? draggingSupport.get("startVertex0X")
        : orient === "horizontal"
          ? draggingSupport.get("startVertex0Y")
          : null;

    // Second (or later) split-drag: prior mid bridge is already a normal wall, so
    // updateDraggingLine clones the mouth vertex and builds a NEW bridge from the
    // abandoned anchor → orphan spur across the bump-out. Strip those only.
    updatedState = this.removeOrphanExtrusionSpurs(
      updatedState,
      layerID,
      draggedLineID,
      orient,
      oldAxis
    );

    return { updatedState };
  }

  /**
   * Remove ONLY short dead-end spur stubs after split-drag.
   * Never delete long walls with a free end — that hid half the building
   * (open drawings, bay tips, unfinished walls).
   */
  static removeOrphanExtrusionSpurs(
    state,
    layerID,
    draggedLineID,
    orient,
    oldAxisCoord = null
  ) {
    const eps = 10;
    const MAX_STUB_LEN = 85;
    const toRemove = [];
    state.getIn(["scene", "layers", layerID, "lines"]).forEach((line, lineID) => {
      if (lineID === draggedLineID) return;
      if (line.getIn(["misc", "_splitBridge"])) return;

      const v0 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(0),
      ]);
      const v1 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(1),
      ]);
      if (!v0 || !v1) return;

      const isH = Math.abs(v0.y - v1.y) <= eps;
      const isV = Math.abs(v0.x - v1.x) <= eps;
      const len = GeometryUtils.verticesDistance(v0, v1);
      const aDangle = v0.lines.size <= 1;
      const bDangle = v1.lines.size <= 1;

      // Short diagonal / stair-step ghosts with a free end.
      if (!isH && !isV) {
        if (len < MAX_STUB_LEN && (aDangle || bDangle)) {
          toRemove.push(lineID);
        }
        return;
      }

      // Both ends connected = real wall — keep.
      if (!aDangle && !bDangle) return;

      // Long free-end walls are intentional (drawing / outer stubs) — keep.
      if (len > MAX_STUB_LEN) return;

      // Prefer stubs on the old extrusion axis when known.
      if (oldAxisCoord != null) {
        if (isH) {
          const onAxis =
            Math.abs(v0.y - oldAxisCoord) <= eps ||
            Math.abs(v1.y - oldAxisCoord) <= eps;
          if (!onAxis && len > 45) return;
        } else if (isV) {
          const onAxis =
            Math.abs(v0.x - oldAxisCoord) <= eps ||
            Math.abs(v1.x - oldAxisCoord) <= eps;
          if (!onAxis && len > 45) return;
        }
      }

      toRemove.push(lineID);
    });

    toRemove.forEach((lineID) => {
      state = Line.remove(state, layerID, lineID).updatedState;
    });

    return state;
  }

  /**
   * After drag, force bridges attached to the dragged wall to be orthogonal by
   * moving ONLY the dragged-wall endpoint (never room corners).
   */
  static snapDraggedJunctionsOrthogonal(state, layerID, draggedLineID, orient) {
    if (orient !== "vertical" && orient !== "horizontal") return state;

    const dragged = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      draggedLineID,
    ]);
    if (!dragged) return state;

    const eps = 1;
    const nearOrthoTol = 12; // px — only straighten near-ortho bridges, never bays
    dragged.vertices.forEach((vid) => {
      const vertex = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        vid,
      ]);
      if (!vertex) return;

      vertex.lines.forEach((connectedID) => {
        if (connectedID === draggedLineID) return;
        const connected = state.getIn([
          "scene",
          "layers",
          layerID,
          "lines",
          connectedID,
        ]);
        if (!connected) return;

        const otherID =
          connected.vertices.get(0) === vid
            ? connected.vertices.get(1)
            : connected.vertices.get(0);
        const other = state.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          otherID,
        ]);
        if (!other) return;

        const live = state.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          vid,
        ]);
        if (!live) return;

        const lineOrient = this.getLineOrientation(
          live.x,
          live.y,
          other.x,
          other.y
        );
        // Never touch intentional diagonals / bay angles.
        if (lineOrient === "diagonal") return;
        // Collinear continuation of dragged wall — leave alone.
        if (lineOrient === orient) return;

        // Only nudge if already nearly orthogonal (tiny skew from bridges).
        const skew =
          orient === "vertical"
            ? Math.abs(live.y - other.y)
            : Math.abs(live.x - other.x);
        if (skew > nearOrthoTol) return;

        const next = { x: live.x, y: live.y };
        if (orient === "vertical") next.y = other.y;
        else next.x = other.x;

        if (
          Math.abs(next.x - live.x) > eps ||
          Math.abs(next.y - live.y) > eps
        ) {
          state = Vertex.setAttributes(
            state,
            layerID,
            vid,
            new Map(next)
          ).updatedState;
        }
      });
    });

    return state;
  }

  /**
   * DISABLED: previously snapped ALL diagonal walls to H/V, which destroyed
   * client bay windows / inverted bay angles whenever any wall drag finished.
   * Intentional angles must be preserved. Split-drag ortho is handled only on
   * _splitBridge walls inside finalizeSplitDrag.
   */
  static repairDiagonalWalls(state, layerID) {
    return state;
  }

  /**
   * After extruding a split wall, delete remnant walls that still close the
   * opening between the two bridge anchors (client "extra wall"). Does not
   * touch extension bridges or stubs that continue outside the opening.
   */
  static removeSplitOpeningWall(
    state,
    layerID,
    draggedLineID,
    anchorA,
    anchorB,
    orient
  ) {
    const va = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      anchorA,
    ]);
    const vb = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      anchorB,
    ]);
    if (!va || !vb) return state;

    const eps = 10;
    const toRemove = [];
    const openingLen = GeometryUtils.verticesDistance(va, vb);

    state.getIn(["scene", "layers", layerID, "lines"]).forEach((line, lineID) => {
      if (lineID === draggedLineID) return;

      const v0 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(0),
      ]);
      const v1 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(1),
      ]);
      if (!v0 || !v1) return;

      const len = GeometryUtils.verticesDistance(v0, v1);
      // Only the remnant that still joins the two opening anchors.
      // (Old bbox test deleted whole facade walls → big transparent split.)
      const d0a = GeometryUtils.verticesDistance(v0, va);
      const d0b = GeometryUtils.verticesDistance(v0, vb);
      const d1a = GeometryUtils.verticesDistance(v1, va);
      const d1b = GeometryUtils.verticesDistance(v1, vb);
      const joinsAnchors =
        (d0a <= eps && d1b <= eps) || (d0b <= eps && d1a <= eps);
      if (!joinsAnchors) return;
      if (len > openingLen + 25) return;

      if (orient === "vertical") {
        if (Math.abs(v0.x - v1.x) > eps) return;
      } else if (orient === "horizontal") {
        if (Math.abs(v0.y - v1.y) > eps) return;
      } else {
        return;
      }
      toRemove.push(lineID);
    });

    toRemove.forEach((lineID) => {
      state = Line.remove(state, layerID, lineID).updatedState;
    });

    return state;
  }

  static reconcileSplitBridges(state, layerID, draggedLineID) {
    let updatedState = state;
    const bridgeIDs = [];

    updatedState
      .getIn(["scene", "layers", layerID, "lines"])
      .forEach((line, lineID) => {
        if (!line.getIn(["misc", "_splitBridge"])) return;

        const v0 = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          line.vertices.get(0),
        ]);
        const v1 = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          line.vertices.get(1),
        ]);
        if (!v0 || !v1) return;

        if (GeometryUtils.verticesDistance(v0, v1) < 3) {
          bridgeIDs.push(lineID);
        }
      });

    bridgeIDs.forEach((bridgeID) => {
      updatedState = Line.remove(updatedState, layerID, bridgeID).updatedState;
    });

    return { updatedState };
  }

  static wallSegmentExists(state, layerID, x0, y0, x1, y1) {
    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
    return state
      .getIn(["scene", "layers", layerID, "lines"])
      .some((line) => {
        const a = vertices.get(line.vertices.get(0));
        const b = vertices.get(line.vertices.get(1));
        if (!a || !b) return false;
        return (
          (GeometryUtils.samePoints(a, { x: x0, y: y0 }) &&
            GeometryUtils.samePoints(b, { x: x1, y: y1 })) ||
          (GeometryUtils.samePoints(a, { x: x1, y: y1 }) &&
            GeometryUtils.samePoints(b, { x: x0, y: y0 }))
        );
      });
  }

  static addFromPoints(state, layerID, type, points, properties, holes) {
    points = new List(points).sort(({ x: x1, y: y1 }, { x: x2, y: y2 }) =>
      x1 === x2 ? y1 - y2 : x1 - x2
    );

    let pointsPair = points
      .zip(points.skip(1))
      .filterNot(
        ([{ x: x1, y: y1 }, { x: x2, y: y2 }]) => x1 === x2 && y1 === y2
      );

    let lines = [];

    pointsPair.forEach(([{ x: x1, y: y1 }, { x: x2, y: y2 }]) => {
      if (this.wallSegmentExists(state, layerID, x1, y1, x2, y2)) {
        return;
      }

      let { updatedState: stateL, line } = this.create(
        state,
        layerID,
        type,
        x1,
        y1,
        x2,
        y2,
        properties
      );
      state = stateL;

      if (holes) {
        holes.forEach((holeWithOffsetPoint) => {
          let { x: xp, y: yp } = holeWithOffsetPoint.offsetPosition;

          if (GeometryUtils.isPointOnLineSegment(x1, y1, x2, y2, xp, yp)) {
            let newOffset = GeometryUtils.pointPositionOnLineSegment(
              x1,
              y1,
              x2,
              y2,
              xp,
              yp
            );

            if (newOffset >= 0 && newOffset <= 1) {
              state = Hole.create(
                state,
                layerID,
                holeWithOffsetPoint.hole.type,
                line.id,
                newOffset,
                holeWithOffsetPoint.hole.properties
              ).updatedState;
            }
          }
        });
      }

      lines.push(line);
    });

    return { updatedState: state, lines: new List(lines) };
  }

  static createAvoidingIntersections(
    state,
    layerID,
    type,
    x0,
    y0,
    x1,
    y1,
    oldProperties,
    oldHoles
  ) {
    let points = [
      { x: x0, y: y0 },
      { x: x1, y: y1 },
    ];

    state = state
      .getIn(["scene", "layers", layerID, "lines"])
      .reduce((reducedState, line) => {
        let [v0, v1] = line.vertices
          .map((vertexID) =>
            reducedState
              .getIn(["scene", "layers", layerID, "vertices"])
              .get(vertexID)
          )
          .toArray();

        if (!v0 || !v1) return reducedState;

        let hasCommonEndpoint =
          GeometryUtils.samePoints(v0, points[0]) ||
          GeometryUtils.samePoints(v0, points[1]) ||
          GeometryUtils.samePoints(v1, points[0]) ||
          GeometryUtils.samePoints(v1, points[1]);

        let intersection = GeometryUtils.twoLineSegmentsIntersection(
          points[0],
          points[1],
          { x: v0.x, y: v0.y },
          { x: v1.x, y: v1.y }
        );

        if (!intersection) return reducedState;

        if (intersection.type === "colinear") {
          if (!oldHoles) {
            oldHoles = [];
          }

          let orderedVertices = GeometryUtils.orderVertices(points);

          reducedState
            .getIn(["scene", "layers", layerID, "lines", line.id, "holes"])
            .forEach((holeID) => {
              let hole = reducedState.getIn([
                "scene",
                "layers",
                layerID,
                "holes",
                holeID,
              ]);
              let oldLineLength = GeometryUtils.pointsDistance(
                v0.x,
                v0.y,
                v1.x,
                v1.y
              );
              let offset = GeometryUtils.samePoints(orderedVertices[1], v1)
                ? 1 - hole.offset
                : hole.offset;
              let offsetPosition = GeometryUtils.extendLine(
                v0.x,
                v0.y,
                v1.x,
                v1.y,
                oldLineLength * offset
              );

              oldHoles.push({ hole, offsetPosition });
            });

          reducedState = this.remove(
            reducedState,
            layerID,
            line.id
          ).updatedState;

          points.push({ x: v0.x, y: v0.y }, { x: v1.x, y: v1.y });
        }

        if (
          intersection.type === "intersecting" &&
          intersection.point &&
          !hasCommonEndpoint
        ) {
          reducedState = this.split(
            reducedState,
            layerID,
            line.id,
            intersection.point.x,
            intersection.point.y,
            { skipSnap: true }
          ).updatedState;
          points.push(intersection.point);
        }

        return reducedState;
      }, state);

    let { updatedState, lines } = Line.addFromPoints(
      state,
      layerID,
      type,
      points,
      oldProperties,
      oldHoles
    );

    return { updatedState, lines };
  }

  static replaceVertex(state, layerID, lineID, vertexIndex, x, y) {
    let vertexID = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      lineID,
      "vertices",
      vertexIndex,
    ]);

    state = Vertex.remove(
      state,
      layerID,
      vertexID,
      "lines",
      lineID
    ).updatedState;
    let { updatedState: stateV, vertex } = Vertex.add(
      state,
      layerID,
      x,
      y,
      "lines",
      lineID
    );
    state = stateV;

    state = state.setIn(
      ["scene", "layers", layerID, "lines", lineID, "vertices", vertexIndex],
      vertex.id
    );
    state = state.setIn(
      ["scene", "layers", layerID, "lines", lineID],
      state.getIn(["scene", "layers", layerID, "lines", lineID])
    );

    return {
      updatedState: state,
      line: state.getIn(["scene", "layers", layerID, "lines", lineID]),
      vertex,
    };
  }

  static selectToolDrawingLine(state, sceneComponentType) {
    state = state.merge({
      mode: MODE_WAITING_DRAWING_LINE,
      drawingSupport: new Map({
        type: sceneComponentType,
      }),
    });

    return { updatedState: state };
  }

  static selectToolDrawingSquare(state, sceneComponentType) {
    state = state.merge({
      mode: MODE_WAITING_DRAWING_SQUARE,
      drawingSupport: new Map({
        type: sceneComponentType,
      }),
    });

    return { updatedState: state };
  }

  static beginDrawingLine(state, layerID, x, y) {
    let snapElements = SnapSceneUtils.sceneSnapElements(
      state.scene,
      new List(),
      state.snapMask
    );
    let snap = null;

    if (state.snapMask && !state.snapMask.isEmpty()) {
      snap = SnapUtils.nearestSnap(snapElements, x, y, state.snapMask);

      if (snap) ({ x, y } = snap.point);

      snapElements = snapElements.withMutations((snapElements) => {
        let a, b, c;
        ({ a, b, c } = GeometryUtils.horizontalLine(y));
        SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
        ({ a, b, c } = GeometryUtils.verticalLine(x));
        SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
      });
    }

    const wallSnap = Layer.snapPointToNearestWall(state, layerID, x, y);
    x = wallSnap.x;
    y = wallSnap.y;
    state = Layer.splitHostWallsAtPoint(state, layerID, x, y).updatedState;

    let drawingSupport = state.get("drawingSupport").set("layerID", layerID);

    state = Layer.unselectAll(state, layerID).updatedState;

    let { updatedState: stateL, line } = Line.create(
      state,
      layerID,
      drawingSupport.get("type"),
      x,
      y,
      x,
      y
    );
    state = Line.select(stateL, layerID, line.id).updatedState;

    state = state.merge({
      mode: MODE_DRAWING_LINE,
      snapElements,
      activeSnapElement: snap ? snap.snap : null,
      drawingSupport,
    });

    return { updatedState: state };
  }

  static updateDrawingLine(state, x, y) {
    let snap = null;
    if (state.snapMask && !state.snapMask.isEmpty()) {
      snap = SnapUtils.nearestSnap(state.snapElements, x, y, state.snapMask);
      if (snap) ({ x, y } = snap.point);
    }

    let layerID = state.getIn(["drawingSupport", "layerID"]);
    let lineID = state
      .getIn(["scene", "layers", layerID, "selected", "lines"])
      .first();

    let { updatedState: stateLV, vertex } = Line.replaceVertex(
      state,
      layerID,
      lineID,
      1,
      x,
      y
    );
    state = stateLV;

    state = this.select(state, layerID, lineID).updatedState;
    state = state.merge({ activeSnapElement: snap ? snap.snap : null });

    return { updatedState: state };
  }

  static endDrawingLine(state, x, y) {
    fraLog("WALL", "endDrawingLine — wall segment finished", { x, y });

    // Already finished / not drawing — never crash on second mouseup.
    if (!state.get("drawingSupport") || state.get("mode") !== MODE_DRAWING_LINE) {
      return {
        updatedState: state.merge({
          mode: MODE_IDLE,
          drawingSupport: null,
          activeSnapElement: null,
          snapElements: new List(),
        }),
        beginDrawing: false,
      };
    }

    if (state.snapMask && !state.snapMask.isEmpty()) {
      let snap = SnapUtils.nearestSnap(
        state.snapElements,
        x,
        y,
        state.snapMask
      );
      if (snap) ({ x, y } = snap.point);
    }

    let layerID = state.getIn(["drawingSupport", "layerID"]);
    let layer = state.getIn(["scene", "layers", layerID]);
    if (!layerID || !layer) {
      return {
        updatedState: state.merge({
          mode: MODE_IDLE,
          drawingSupport: null,
          activeSnapElement: null,
          snapElements: new List(),
        }),
        beginDrawing: false,
      };
    }

    let lineID = state
      .getIn(["scene", "layers", layerID, "selected", "lines"])
      .first();
    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    if (!line) {
      return {
        updatedState: state.merge({
          mode: MODE_IDLE,
          drawingSupport: null,
          activeSnapElement: null,
          snapElements: new List(),
        }),
        beginDrawing: false,
      };
    }

    let v0 = layer.vertices.get(line.vertices.get(0));
    if (!v0) {
      return {
        updatedState: state.merge({
          mode: MODE_IDLE,
          drawingSupport: null,
          activeSnapElement: null,
          snapElements: new List(),
        }),
        beginDrawing: false,
      };
    }

    const lineType = line.type;
    const startX = v0.x;
    const startY = v0.y;

    state = Line.remove(state, layerID, lineID).updatedState;

    state = state.merge({
      mode: MODE_IDLE,
      drawingSupport: null,
      activeSnapElement: null,
      snapElements: new List(),
    });

    if (!GeometryUtils.samePoints({ x: startX, y: startY }, { x, y })) {
      const createResult = Line.createAvoidingIntersections(
        state,
        layerID,
        lineType,
        startX,
        startY,
        x,
        y
      );
      state = createResult.updatedState;

      if (createResult.lines && !createResult.lines.isEmpty()) {
        createResult.lines.forEach((createdLine) => {
          state = this.straightenLineIfNearOrtho(
            state,
            layerID,
            createdLine.id,
            12
          );
          state = Layer.mergeEqualsVertices(
            state,
            layerID,
            createdLine.vertices.get(0)
          ).updatedState;
          state = Layer.mergeEqualsVertices(
            state,
            layerID,
            createdLine.vertices.get(1)
          ).updatedState;
        });
      } else if (
        !Line.wallSegmentExists(state, layerID, startX, startY, x, y)
      ) {
        const fallback = Line.create(
          state,
          layerID,
          lineType,
          startX,
          startY,
          x,
          y
        );
        state = fallback.updatedState;
        if (fallback.line) {
          state = this.straightenLineIfNearOrtho(
            state,
            layerID,
            fallback.line.id,
            12
          );
          state = Layer.mergeEqualsVertices(
            state,
            layerID,
            fallback.line.vertices.get(0)
          ).updatedState;
          state = Layer.mergeEqualsVertices(
            state,
            layerID,
            fallback.line.vertices.get(1)
          ).updatedState;
        }
      }
    }

    state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    state = Layer.removeDuplicateLines(state, layerID).updatedState;
    // Close near-miss gaps so rooms aren't transparent / area 0.
    state = this.healFreeEndsOnLayer(state, layerID, 140);

    let { updatedState } = Layer.detectAndUpdateAreas(state, layerID);
    updatedState = Layer.ensureSolidRoomFill(updatedState, layerID).updatedState;
    updatedState = updatedState.update("splittedVertices", () => new List([]));
    const updatedAreas = updatedState.getIn([
      "scene",
      "layers",
      layerID,
      "areas",
    ]);
    const beginDrawing =
      updatedAreas.size ==
      state.getIn(["scene", "layers", layerID, "areas"]).size;
    state = updatedState;
    state = Layer.unselectAll(state, layerID).updatedState;

    return { updatedState: state, beginDrawing: beginDrawing };
  }

  static endDrawingSquare(state, layerID, x, y) {
    fraLog("ROOM", "endDrawingSquare — mouse released, finalizing room", {
      layerID,
      x,
      y,
    });
    if (state.snapMask && !state.snapMask.isEmpty()) {
      let snap = SnapUtils.nearestSnap(
        state.snapElements,
        x,
        y,
        state.snapMask
      );
      if (snap) ({ x, y } = snap.point);
    }

    // Snap final corner, then detect the closed rectangular room.
    state = Line.updateDraggingSquare(state, x, y).updatedState;

    const draggingSupport = state.get("draggingSupport");
    const cornerVertexIDs = draggingSupport
      ? draggingSupport.get("cornerVertexIDs")
      : null;
    if (draggingSupport) {
      layerID = draggingSupport.get("layerID");
    }

    state = state.merge({
      mode: MODE_IDLE,
      draggingSupport: null,
      activeSnapElement: null,
      snapElements: new List(),
    });

    state.getIn(["scene", "layers", layerID, "vertices"]).forEach((vertex) => {
      state = Layer.mergeEqualsVertices(state, layerID, vertex.id).updatedState;
    });

    state = Layer.detectAndUpdateAreas(state, layerID).updatedState;

    if (state.getIn(["scene", "layers", layerID, "areas"]).size === 0) {
      state = Layer.ensureRectangularAreaFromCorners(
        state,
        layerID,
        cornerVertexIDs
      );
    }

    state = Layer.ensureSolidRoomFill(state, layerID).updatedState;
    state = Layer.unselectAll(state, layerID).updatedState;

    state = state.updateIn(
      ["scene", "layers", layerID, "areas"],
      (areas) =>
        areas.map((a) =>
          a.set("visible", true).setIn(["properties", "opacity"], 100)
        )
    );

    const areaCount = state.getIn(["scene", "layers", layerID, "areas"]).size;
    fraLog("ROOM", "endDrawingSquare DONE — mode is IDLE, areas count", {
      areaCount,
      mode: state.get("mode"),
    });

    return { updatedState: state };
  }

  static beginDraggingLine(state, layerID, lineID, x, y) {
    let snapElements = SnapSceneUtils.sceneSnapElements(
      state.scene,
      new List(),
      state.snapMask
    );

    let layer = state.scene.layers.get(layerID);
    let line = layer.lines.get(lineID);
    if (!line) return { updatedState: state };

    let vertex0 = layer.vertices.get(line.vertices.get(0));
    let vertex1 = layer.vertices.get(line.vertices.get(1));
    if (!vertex0 || !vertex1) return { updatedState: state };

    const splitVertices = state.get("splittedVertices") || new List();
    const lineOrientation = this.getLineOrientation(
      vertex0.x,
      vertex0.y,
      vertex1.x,
      vertex1.y
    );
    // ONLY detach after an explicit right-click Split (vertex in splittedVertices).
    // isSplitMiddleAnchor on every collinear T-junction caused normal wall moves
    // (and post-split segment moves) to run extrusion cleanup and HIDE walls.
    const pendingDetachIndexes = [];
    [0, 1].forEach((vertexIndex) => {
      const vertexID = line.vertices.get(vertexIndex);
      const vertex = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        vertexID,
      ]);
      if (!vertex || vertex.lines.size <= 1) return;
      if (splitVertices.includes(vertexID)) {
        pendingDetachIndexes.push(vertexIndex);
      }
    });

    state = state.merge({
      mode: MODE_DRAGGING_LINE,
      snapElements,
      draggingSupport: Map({
        layerID,
        lineID,
        startPointX: x,
        startPointY: y,
        startVertex0X: vertex0.x,
        startVertex0Y: vertex0.y,
        startVertex1X: vertex1.x,
        startVertex1Y: vertex1.y,
        lineOrientation,
        sharedVertices: [],
        anchorClones: [],
        pendingDetachIndexes,
        isSplitDrag: pendingDetachIndexes.length > 0,
        detachApplied: false,
      }),
    });

    fraSplitSnapshot(state, layerID, "split drag begin", {
      lineID,
      pendingDetachIndexes,
    });

    return { updatedState: state };
  }

  static getLineOrientation(x1, y1, x2, y2) {
    return wallOrient(x1, y1, x2, y2);
  }

  /**
   * True if placing a wall at (x0,y0)-(x1,y1) would sit on top of another
   * parallel wall (same axis overlap). That snap was hiding the opposite
   * "top wall" when dragging a shared corridor wall with Snap LN on.
   */
  static wouldCoincideWithParallelWall(
    state,
    layerID,
    lineID,
    x0,
    y0,
    x1,
    y1
  ) {
    const layer = state.getIn(["scene", "layers", layerID]);
    if (!layer) return false;
    const lines = [];
    layer.get("lines").forEach((other, otherID) => {
      const a = layer.getIn(["vertices", other.vertices.get(0)]);
      const b = layer.getIn(["vertices", other.vertices.get(1)]);
      if (!a || !b) return;
      lines.push({
        id: otherID,
        v0: { x: a.x, y: a.y },
        v1: { x: b.x, y: b.y },
        isBridge: !!other.getIn(["misc", "_splitBridge"]),
      });
    });
    return wouldCoincideWithParallelWalls(lines, lineID, x0, y0, x1, y1);
  }

  /** Fix 1–10px skew on walls that should be H/V (client translate/skew bug). */
  static straightenLineIfNearOrtho(state, layerID, lineID, tol = 10) {
    const line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    if (!line) return state;
    const id0 = line.vertices.get(0);
    const id1 = line.vertices.get(1);
    const v0 = state.getIn(["scene", "layers", layerID, "vertices", id0]);
    const v1 = state.getIn(["scene", "layers", layerID, "vertices", id1]);
    if (!v0 || !v1) return state;
    const next = straightenNearOrthoEndpoints(v0.x, v0.y, v1.x, v1.y, tol);
    if (!next.straightened) return state;
    state = Vertex.setAttributes(
      state,
      layerID,
      id0,
      new Map({ x: next.x0, y: next.y0 })
    ).updatedState;
    state = Vertex.setAttributes(
      state,
      layerID,
      id1,
      new Map({ x: next.x1, y: next.y1 })
    ).updatedState;
    return state;
  }

  /** Pull free endpoints onto nearby vertices so rooms can close (area ≠ 0). */
  static snapLineEndsToNearbyVertices(state, layerID, lineID, tol = 18) {
    const line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    if (!line) return state;
    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
    [0, 1].forEach((idx) => {
      const vid = line.vertices.get(idx);
      const v = state.getIn(["scene", "layers", layerID, "vertices", vid]);
      if (!v) return;
      let bestId = null;
      let bestDist = tol;
      vertices.forEach((other, otherId) => {
        if (otherId === vid) return;
        const d = Math.hypot(other.x - v.x, other.y - v.y);
        if (d < bestDist) {
          bestDist = d;
          bestId = otherId;
        }
      });
      if (!bestId) return;
      const target = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        bestId,
      ]);
      if (!target) return;
      state = Vertex.setAttributes(
        state,
        layerID,
        vid,
        new Map({ x: target.x, y: target.y })
      ).updatedState;
      state = Layer.mergeEqualsVertices(state, layerID, vid).updatedState;
    });
    return state;
  }

  /**
   * When a free end lands just past a host wall tip, stretch that tip to the
   * join point so the corner actually welds (fixes back-wall big split gaps).
   */
  static extendNearestHostEndToPoint(
    state,
    layerID,
    px,
    py,
    excludeLineId,
    tol
  ) {
    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
    let best = null;

    state.getIn(["scene", "layers", layerID, "lines"]).forEach((line, lineID) => {
      if (lineID === excludeLineId) return;
      const ids = [line.vertices.get(0), line.vertices.get(1)];
      ids.forEach((endId, idx) => {
        const end = vertices.get(endId);
        if (!end) return;
        // Prefer free tips (or any tip very near the target).
        const dist = Math.hypot(end.x - px, end.y - py);
        if (dist < 0.5 || dist > tol) return;
        const other = vertices.get(ids[1 - idx]);
        if (!other) return;
        const orient = this.getLineOrientation(other.x, other.y, end.x, end.y);
        // Stay on wall axis.
        let nx = end.x;
        let ny = end.y;
        if (orient === "horizontal") {
          if (Math.abs(end.y - py) > 12) return;
          nx = px;
          ny = end.y;
        } else if (orient === "vertical") {
          if (Math.abs(end.x - px) > 12) return;
          nx = end.x;
          ny = py;
        } else {
          return;
        }
        const move = Math.hypot(nx - end.x, ny - end.y);
        if (move < 0.5 || move > tol) return;
        if (!best || dist < best.dist) {
          best = { endId, nx, ny, lineID, dist };
        }
      });
    });

    if (!best) return state;
    state = Vertex.setAttributes(
      state,
      layerID,
      best.endId,
      new Map({ x: best.nx, y: best.ny })
    ).updatedState;
    state = Layer.mergeEqualsVertices(state, layerID, best.endId).updatedState;
    state = this.straightenLineIfNearOrtho(state, layerID, best.lineID, 12);
    return state;
  }

  /**
   * Heal disconnects WITHOUT making walls diagonal:
   * - L-corners: two free ends (H+V) meet at (Vx, Hy)
   * - T-junctions: free end projects onto a perpendicular host wall
   * Keeps back-wall / side-wall splits from leaving transparent rooms (area 0).
   * (Old bbox wipe caused big transparent split — heal instead of delete.)
   */
  static healFreeEndsOnLayer(state, layerID, tol = 120) {
    const lines = state.getIn(["scene", "layers", layerID, "lines"]);
    if (!lines) return state;

    const freeEndVertexIds = [];
    state.getIn(["scene", "layers", layerID, "vertices"]).forEach((v, vid) => {
      if (v && v.lines && v.lines.size === 1) freeEndVertexIds.push(vid);
    });

    const ownerMeta = (vid) => {
      const v = state.getIn(["scene", "layers", layerID, "vertices", vid]);
      if (!v || !v.lines || v.lines.size !== 1) return null;
      const ownerLineId = v.lines.get(0);
      const line = state.getIn([
        "scene",
        "layers",
        layerID,
        "lines",
        ownerLineId,
      ]);
      if (!line) return null;
      const otherId =
        line.vertices.get(0) === vid
          ? line.vertices.get(1)
          : line.vertices.get(0);
      const other = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        otherId,
      ]);
      if (!other) return null;
      const orient = this.getLineOrientation(other.x, other.y, v.x, v.y);
      return { v, ownerLineId, other, orient };
    };

    const applySnap = (vid, x, y, ownerLineId) => {
      const cur = state.getIn(["scene", "layers", layerID, "vertices", vid]);
      if (!cur) return;
      const moved = Math.hypot(x - cur.x, y - cur.y);
      if (moved < 0.5 || moved > tol * 1.5) return;
      state = Vertex.setAttributes(
        state,
        layerID,
        vid,
        new Map({ x, y })
      ).updatedState;
      state = Layer.mergeEqualsVertices(state, layerID, vid).updatedState;
      // If join is just past a host endpoint, pull that endpoint to the corner.
      state = this.extendNearestHostEndToPoint(
        state,
        layerID,
        x,
        y,
        ownerLineId,
        tol
      );
      state = Layer.splitHostWallsAtPoint(
        state,
        layerID,
        x,
        y,
        Math.max(tol, 100)
      ).updatedState;
      if (ownerLineId) {
        state = this.straightenLineIfNearOrtho(state, layerID, ownerLineId, 12);
      }
    };

    // Pass 1 — close near-miss L corners (H free end + V free end → (Vx, Hy)).
    for (let i = 0; i < freeEndVertexIds.length; i++) {
      for (let j = i + 1; j < freeEndVertexIds.length; j++) {
        const aId = freeEndVertexIds[i];
        const bId = freeEndVertexIds[j];
        const a = ownerMeta(aId);
        const b = ownerMeta(bId);
        if (!a || !b) continue;
        if (a.ownerLineId === b.ownerLineId) continue;

        let corner = null;
        if (a.orient === "vertical" && b.orient === "horizontal") {
          corner = { x: a.v.x, y: b.v.y };
        } else if (a.orient === "horizontal" && b.orient === "vertical") {
          corner = { x: b.v.x, y: a.v.y };
        } else {
          continue;
        }

        const da = Math.hypot(corner.x - a.v.x, corner.y - a.v.y);
        const db = Math.hypot(corner.x - b.v.x, corner.y - b.v.y);
        if (da > tol || db > tol) continue;
        if (da < 0.5 && db < 0.5) continue;

        applySnap(aId, corner.x, corner.y, a.ownerLineId);
        applySnap(bId, corner.x, corner.y, b.ownerLineId);
      }
    }

    // Refresh free ends after corner welds.
    freeEndVertexIds.length = 0;
    state.getIn(["scene", "layers", layerID, "vertices"]).forEach((v, vid) => {
      if (v && v.lines && v.lines.size === 1) freeEndVertexIds.push(vid);
    });

    // Pass 2 — ortho T / end snap onto perpendicular host walls only.
    freeEndVertexIds.forEach((vid) => {
      const meta = ownerMeta(vid);
      if (!meta) return;
      const { v, ownerLineId, orient } = meta;
      if (orient !== "vertical" && orient !== "horizontal") return;

      let best = null;
      const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
      const allLines = state.getIn(["scene", "layers", layerID, "lines"]);

      // Prefer other vertices that keep this wall ortho (axis within tol).
      vertices.forEach((cand, candId) => {
        if (candId === vid) return;
        if (orient === "vertical") {
          if (Math.abs(cand.x - v.x) > tol) return;
          const target = { x: v.x, y: cand.y };
          // Slight X nudge onto exact corner when within tol.
          if (Math.abs(cand.x - v.x) <= 12) {
            target.x = cand.x;
          }
          const dist = Math.hypot(target.x - v.x, target.y - v.y);
          if (dist < 0.5 || dist > tol) return;
          if (!best || dist < best.dist) best = { ...target, dist };
        } else {
          if (Math.abs(cand.y - v.y) > tol) return;
          const target = { x: cand.x, y: v.y };
          if (Math.abs(cand.y - v.y) <= 12) {
            target.y = cand.y;
          }
          const dist = Math.hypot(target.x - v.x, target.y - v.y);
          if (dist < 0.5 || dist > tol) return;
          if (!best || dist < best.dist) best = { ...target, dist };
        }
      });

      // Project onto perpendicular host segments (extend slightly past ends).
      const extendTol = tol;
      allLines.forEach((hostLine, hostId) => {
        if (hostId === ownerLineId) return;
        const h0 = vertices.get(hostLine.vertices.get(0));
        const h1 = vertices.get(hostLine.vertices.get(1));
        if (!h0 || !h1) return;
        const hostOrient = this.getLineOrientation(h0.x, h0.y, h1.x, h1.y);
        if (orient === "vertical" && hostOrient !== "horizontal") return;
        if (orient === "horizontal" && hostOrient !== "vertical") return;

        let target;
        if (orient === "vertical") {
          // Meet horizontal host at (v.x, hostY).
          const hostY = (h0.y + h1.y) / 2;
          if (Math.abs(h0.y - h1.y) > 12) return;
          const minX = Math.min(h0.x, h1.x) - extendTol;
          const maxX = Math.max(h0.x, h1.x) + extendTol;
          if (v.x < minX || v.x > maxX) return;
          target = { x: v.x, y: hostY };
        } else {
          const hostX = (h0.x + h1.x) / 2;
          if (Math.abs(h0.x - h1.x) > 12) return;
          const minY = Math.min(h0.y, h1.y) - extendTol;
          const maxY = Math.max(h0.y, h1.y) + extendTol;
          if (v.y < minY || v.y > maxY) return;
          target = { x: hostX, y: v.y };
        }

        const dist = Math.hypot(target.x - v.x, target.y - v.y);
        if (dist < 0.5 || dist > tol) return;
        if (!best || dist < best.dist) best = { ...target, dist };
      });

      if (!best) return;
      applySnap(vid, best.x, best.y, ownerLineId);
    });

    state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    return state;
  }

  static orientationsPerpendicular(a, b) {
    // Diagonal connected walls still need detach+bridge (treat as needing fix).
    if (a === "diagonal" || b === "diagonal") return true;
    return a !== b;
  }

  static classifyConnectedLinesAtVertex(
    state,
    layerID,
    vertexID,
    excludeLineID,
    dragOrientation,
    excludeDragBridges = false
  ) {
    const vertex = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      vertexID,
    ]);
    const perpendicular = [];
    const parallel = [];

    if (!vertex) return { perpendicular, parallel };

    vertex.lines.forEach((connectedLineID) => {
      if (connectedLineID === excludeLineID) return;

      const connectedLine = state.getIn([
        "scene",
        "layers",
        layerID,
        "lines",
        connectedLineID,
      ]);
      if (!connectedLine) return;
      if (
        excludeDragBridges &&
        (connectedLine.getIn(["misc", "_dragBridge"]) ||
          connectedLine.getIn(["misc", "_splitBridge"]))
      ) {
        return;
      }

      const cv0 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        connectedLine.vertices.get(0),
      ]);
      const cv1 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        connectedLine.vertices.get(1),
      ]);
      if (!cv0 || !cv1) return;

      const lineOrient = this.getLineOrientation(cv0.x, cv0.y, cv1.x, cv1.y);

      if (this.orientationsPerpendicular(lineOrient, dragOrientation)) {
        perpendicular.push(connectedLineID);
      } else if (lineOrient === dragOrientation) {
        parallel.push(connectedLineID);
      }
    });

    return { perpendicular, parallel };
  }

  static computeDraggedLinePositions(state, draggingSupport, x, y) {
    const layerID = draggingSupport.get("layerID");
    const lineID = draggingSupport.get("lineID");
    let diffX = x - draggingSupport.get("startPointX");
    let diffY = y - draggingSupport.get("startPointY");
    const line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
    const v0 = vertices.get(line.vertices.get(0));
    const v1 = vertices.get(line.vertices.get(1));
    const orientation =
      draggingSupport.get("lineOrientation") ||
      this.getLineOrientation(v0.x, v0.y, v1.x, v1.y);

    if (orientation === "vertical") diffY = 0;
    else if (orientation === "horizontal") diffX = 0;

    let newVertex0X = draggingSupport.get("startVertex0X") + diffX;
    let newVertex0Y = draggingSupport.get("startVertex0Y") + diffY;
    let newVertex1X = draggingSupport.get("startVertex1X") + diffX;
    let newVertex1Y = draggingSupport.get("startVertex1Y") + diffY;

    let activeSnapElement = null;
    if (state.snapMask && !state.snapMask.isEmpty()) {
      const snapElements = state.snapElements;
      const curSnap0 = SnapUtils.nearestSnap(
        snapElements,
        newVertex0X,
        newVertex0Y,
        state.snapMask
      );
      const curSnap1 = SnapUtils.nearestSnap(
        snapElements,
        newVertex1X,
        newVertex1Y,
        state.snapMask
      );

      let deltaX = 0;
      let deltaY = 0;
      if (curSnap0 && curSnap1) {
        if (curSnap0.point.distance < curSnap1.point.distance) {
          deltaX = curSnap0.point.x - newVertex0X;
          deltaY = curSnap0.point.y - newVertex0Y;
          activeSnapElement = curSnap0.snap;
        } else {
          deltaX = curSnap1.point.x - newVertex1X;
          deltaY = curSnap1.point.y - newVertex1Y;
          activeSnapElement = curSnap1.snap;
        }
      } else {
        if (curSnap0) {
          deltaX = curSnap0.point.x - newVertex0X;
          deltaY = curSnap0.point.y - newVertex0Y;
          activeSnapElement = curSnap0.snap;
        }
        if (curSnap1) {
          deltaX = curSnap1.point.x - newVertex1X;
          deltaY = curSnap1.point.y - newVertex1Y;
          activeSnapElement = curSnap1.snap;
        }
      }

      // Keep snap on wall axis so split bridges stay H/V (no diagonal angles).
      if (orientation === "vertical") deltaY = 0;
      else if (orientation === "horizontal") deltaX = 0;

      newVertex0X += deltaX;
      newVertex0Y += deltaY;
      newVertex1X += deltaX;
      newVertex1Y += deltaY;

      if (orientation === "vertical") {
        newVertex0Y = draggingSupport.get("startVertex0Y");
        newVertex1Y = draggingSupport.get("startVertex1Y");
      } else if (orientation === "horizontal") {
        newVertex0X = draggingSupport.get("startVertex0X");
        newVertex1X = draggingSupport.get("startVertex1X");
      }

      if (
        (deltaX !== 0 || deltaY !== 0) &&
        this.wouldCoincideWithParallelWall(
          state,
          layerID,
          lineID,
          newVertex0X,
          newVertex0Y,
          newVertex1X,
          newVertex1Y
        )
      ) {
        newVertex0X -= deltaX;
        newVertex0Y -= deltaY;
        newVertex1X -= deltaX;
        newVertex1Y -= deltaY;
        if (orientation === "vertical") {
          newVertex0Y = draggingSupport.get("startVertex0Y");
          newVertex1Y = draggingSupport.get("startVertex1Y");
        } else if (orientation === "horizontal") {
          newVertex0X = draggingSupport.get("startVertex0X");
          newVertex1X = draggingSupport.get("startVertex1X");
        }
        activeSnapElement = null;
      }
    }

    return {
      newVertex0X,
      newVertex0Y,
      newVertex1X,
      newVertex1Y,
      orientation,
      activeSnapElement,
    };
  }

  static syncProtrusionPreview(
    state,
    layerID,
    orientation,
    lineType,
    lineProps,
    detachedJunctions,
    newVertex0X,
    newVertex0Y,
    newVertex1X,
    newVertex1Y,
    protrusionStubs
  ) {
    let stubs = (protrusionStubs || new List()).toJS
      ? (protrusionStubs || new List()).toJS()
      : [...(protrusionStubs || [])];

    detachedJunctions.forEach((junction) => {
      const vertexIndex = junction.get
        ? junction.get("vertexIndex")
        : junction.vertexIndex;
      const originalVertexId = junction.get
        ? junction.get("originalVertexId")
        : junction.originalVertexId;
      const { originalX, originalY, stubEndX, stubEndY } =
        this.computeJunctionStubEndpoint(
          junction,
          orientation,
          newVertex0X,
          newVertex0Y,
          newVertex1X,
          newVertex1Y
        );

      if (
        GeometryUtils.samePoints(
          { x: originalX, y: originalY },
          { x: stubEndX, y: stubEndY }
        )
      ) {
        return;
      }

      const existingStub = stubs.find(
        (stub) => stub.originalVertexId === originalVertexId
      );

      const canUpdateExistingStub =
        existingStub &&
        existingStub.stubLineId &&
        this.isConnectorStubLine(
          state,
          layerID,
          existingStub.stubLineId,
          originalVertexId,
          orientation
        );

      if (canUpdateExistingStub && existingStub.endVertexId) {
        const endVertex = state.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          existingStub.endVertexId,
        ]);
        if (endVertex) {
          state = Vertex.setAttributes(
            state,
            layerID,
            existingStub.endVertexId,
            new Map({ x: stubEndX, y: stubEndY })
          ).updatedState;
        }
      } else if (
        !Line.wallSegmentExists(
          state,
          layerID,
          originalX,
          originalY,
          stubEndX,
          stubEndY
        )
      ) {
        if (existingStub) {
          stubs = stubs.filter(
            (stub) => stub.originalVertexId !== originalVertexId
          );
        }
        const created = Line.createProtrusionStub(
          state,
          layerID,
          lineType,
          originalVertexId,
          stubEndX,
          stubEndY,
          lineProps,
          orientation
        );
        state = created.updatedState;
        if (created.line) {
          stubs.push({
            originalVertexId,
            vertexIndex,
            stubLineId: created.line.id,
            startVertexId: created.startVertexId,
            endVertexId: created.endVertexId,
          });
        }
      }
    });

    return { updatedState: state, protrusionStubs: fromJS(stubs) };
  }

  static finalizeLineDragProtrusion(
    state,
    layerID,
    orientation,
    lineType,
    lineProps,
    detachedJunctions,
    newVertex0X,
    newVertex0Y,
    newVertex1X,
    newVertex1Y
  ) {
    detachedJunctions.forEach((junction) => {
      const vertexIndex = junction.get
        ? junction.get("vertexIndex")
        : junction.vertexIndex;
      const originalVertexId = junction.get
        ? junction.get("originalVertexId")
        : junction.originalVertexId;
      const originalX = junction.get
        ? junction.get("originalX")
        : junction.originalX;
      const originalY = junction.get
        ? junction.get("originalY")
        : junction.originalY;
      const endX = vertexIndex === 0 ? newVertex0X : newVertex1X;
      const endY = vertexIndex === 0 ? newVertex0Y : newVertex1Y;

      let targetX = originalX;
      let targetY = originalY;
      if (orientation === "vertical") targetX = endX;
      else if (orientation === "horizontal") targetY = endY;
      else {
        targetX = endX;
        targetY = endY;
      }

      if (
        GeometryUtils.samePoints(
          { x: originalX, y: originalY },
          { x: targetX, y: targetY }
        )
      ) {
        return;
      }

      if (
        Line.wallSegmentExists(
          state,
          layerID,
          originalX,
          originalY,
          targetX,
          targetY
        )
      ) {
        return;
      }

      const created = Line.createProtrusionStub(
        state,
        layerID,
        lineType,
        originalVertexId,
        targetX,
        targetY,
        lineProps,
        orientation
      );
      if (created.updatedState) {
        state = created.updatedState;
      }
    });

    return state;
  }

  // static updateDraggingLine(state, x, y) {
  //   let draggingSupport = state.draggingSupport;
  //   let snapElements = state.snapElements;
  //   let layerID = draggingSupport.get("layerID");
  //   let lineID = draggingSupport.get("lineID");
  //   let diffX = x - draggingSupport.get("startPointX");
  //   let diffY = y - draggingSupport.get("startPointY");
  //   const line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
  //   const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
  //   let { x: x1, y: y1 } = vertices.get(line.vertices.get(0));
  //   let { x: x2, y: y2 } = vertices.get(line.vertices.get(1));
  //   let orientation = this.getLineOrientation(x1, y1, x2, y2);
  //   // Apply axis restriction
  //   if (orientation === "vertical") {
  //     diffY = 0; // Only move along X-axis
  //   } else if (orientation === "horizontal") {
  //     diffX = 0; // Only move along Y-axis
  //   }

  //   let newVertex0X = draggingSupport.get("startVertex0X") + diffX;
  //   let newVertex0Y = draggingSupport.get("startVertex0Y") + diffY;
  //   let newVertex1X = draggingSupport.get("startVertex1X") + diffX;
  //   let newVertex1Y = draggingSupport.get("startVertex1Y") + diffY;

  //   let activeSnapElement = null;
  //   let curSnap0 = null,
  //     curSnap1 = null;
  //   if (state.snapMask && !state.snapMask.isEmpty()) {
  //     curSnap0 = SnapUtils.nearestSnap(
  //       snapElements,
  //       newVertex0X,
  //       newVertex0Y,
  //       state.snapMask
  //     );
  //     curSnap1 = SnapUtils.nearestSnap(
  //       snapElements,
  //       newVertex1X,
  //       newVertex1Y,
  //       state.snapMask
  //     );
  //   }

  //   let deltaX = 0,
  //     deltaY = 0;
  //   if (curSnap0 && curSnap1) {
  //     if (curSnap0.point.distance < curSnap1.point.distance) {
  //       deltaX = curSnap0.point.x - newVertex0X;
  //       deltaY = curSnap0.point.y - newVertex0Y;
  //       activeSnapElement = curSnap0.snap;
  //     } else {
  //       deltaX = curSnap1.point.x - newVertex1X;
  //       deltaY = curSnap1.point.y - newVertex1Y;
  //       activeSnapElement = curSnap1.snap;
  //     }
  //   } else {
  //     if (curSnap0) {
  //       deltaX = curSnap0.point.x - newVertex0X;
  //       deltaY = curSnap0.point.y - newVertex0Y;
  //       activeSnapElement = curSnap0.snap;
  //     }
  //     if (curSnap1) {
  //       deltaX = curSnap1.point.x - newVertex1X;
  //       deltaY = curSnap1.point.y - newVertex1Y;
  //       activeSnapElement = curSnap1.snap;
  //     }
  //   }

  //   newVertex0X += deltaX;
  //   newVertex0Y += deltaY;
  //   newVertex1X += deltaX;
  //   newVertex1Y += deltaY;

  //   state = state.merge({
  //     activeSnapElement,
  //     scene: state.scene.updateIn(["layers", layerID], (layer) =>
  //       layer.withMutations((layer) => {
  //         let lineVertices = layer.getIn(["lines", lineID, "vertices"]);
  //         layer.updateIn(["vertices", lineVertices.get(0)], (vertex) =>
  //           vertex.merge({ x: newVertex0X, y: newVertex0Y })
  //         );
  //         layer.updateIn(["vertices", lineVertices.get(1)], (vertex) =>
  //           vertex.merge({ x: newVertex1X, y: newVertex1Y })
  //         );
  //         return layer;
  //       })
  //     ),
  //   });

  //   return { updatedState: state };
  // }

  static updateDraggingLine(state, x, y) {
    let draggingSupport = state.draggingSupport;
    if (!draggingSupport) return { updatedState: state };

    let snapElements = state.snapElements;
    let layerID = draggingSupport.get("layerID");
    let lineID = draggingSupport.get("lineID");

    // Apply deferred split detach on first real move only.
    if (
      !draggingSupport.get("detachApplied") &&
      draggingSupport.get("pendingDetachIndexes") &&
      draggingSupport.get("pendingDetachIndexes").length > 0
    ) {
      const moveFromStart = Math.hypot(
        x - draggingSupport.get("startPointX"),
        y - draggingSupport.get("startPointY")
      );
      if (moveFromStart >= 5) {
        let sharedVertices = [];
        let anchorClones = [];
        const indexes = draggingSupport.get("pendingDetachIndexes");
        const indexList = indexes.toArray ? indexes.toArray() : indexes;
        indexList.forEach((vertexIndex) => {
          const detach = Vertex.detachForLine(
            state,
            layerID,
            lineID,
            vertexIndex
          );
          state = detach.updatedState;
          if (detach.detached) {
            sharedVertices.push(detach.originalVertexId);
            anchorClones.push({
              anchorId: detach.originalVertexId,
              cloneId: detach.vertexID,
            });
          }
        });
        const liveLine = state.getIn([
          "scene",
          "layers",
          layerID,
          "lines",
          lineID,
        ]);
        const lv0 = state.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          liveLine.vertices.get(0),
        ]);
        const lv1 = state.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          liveLine.vertices.get(1),
        ]);
        state = state.merge({
          draggingSupport: draggingSupport.merge({
            sharedVertices,
            anchorClones,
            detachApplied: true,
            isSplitDrag: true,
            startVertex0X: lv0.x,
            startVertex0Y: lv0.y,
            startVertex1X: lv1.x,
            startVertex1Y: lv1.y,
            startPointX: x,
            startPointY: y,
          }),
        });
        draggingSupport = state.draggingSupport;
      }
    }

    let diffX = x - draggingSupport.get("startPointX");
    let diffY = y - draggingSupport.get("startPointY");
    const line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    if (!line) return { updatedState: state };

    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);
    const v0 = vertices.get(line.vertices.get(0));
    const v1 = vertices.get(line.vertices.get(1));
    if (!v0 || !v1) return { updatedState: state };

    let { x: x1, y: y1 } = v0;
    let { x: x2, y: y2 } = v1;
    // Lock to original wall axis — never let snap create diagonal bridges/angles.
    let orientation =
      draggingSupport.get("lineOrientation") ||
      this.getLineOrientation(x1, y1, x2, y2);

    if (orientation === "vertical") {
      diffY = 0;
    } else if (orientation === "horizontal") {
      diffX = 0;
    }

    let newVertex0X = draggingSupport.get("startVertex0X") + diffX;
    let newVertex0Y = draggingSupport.get("startVertex0Y") + diffY;
    let newVertex1X = draggingSupport.get("startVertex1X") + diffX;
    let newVertex1Y = draggingSupport.get("startVertex1Y") + diffY;

    let activeSnapElement = null;
    let curSnap0 = null;
    let curSnap1 = null;
    if (state.snapMask && !state.snapMask.isEmpty()) {
      curSnap0 = SnapUtils.nearestSnap(
        snapElements,
        newVertex0X,
        newVertex0Y,
        state.snapMask
      );
      curSnap1 = SnapUtils.nearestSnap(
        snapElements,
        newVertex1X,
        newVertex1Y,
        state.snapMask
      );
    }

    let deltaX = 0;
    let deltaY = 0;
    if (curSnap0 && curSnap1) {
      if (curSnap0.point.distance < curSnap1.point.distance) {
        deltaX = curSnap0.point.x - newVertex0X;
        deltaY = curSnap0.point.y - newVertex0Y;
        activeSnapElement = curSnap0.snap;
      } else {
        deltaX = curSnap1.point.x - newVertex1X;
        deltaY = curSnap1.point.y - newVertex1Y;
        activeSnapElement = curSnap1.snap;
      }
    } else {
      if (curSnap0) {
        deltaX = curSnap0.point.x - newVertex0X;
        deltaY = curSnap0.point.y - newVertex0Y;
        activeSnapElement = curSnap0.snap;
      }
      if (curSnap1) {
        deltaX = curSnap1.point.x - newVertex1X;
        deltaY = curSnap1.point.y - newVertex1Y;
        activeSnapElement = curSnap1.snap;
      }
    }

    // Snap must stay on the same axis or bridge walls become diagonal angles.
    if (orientation === "vertical") deltaY = 0;
    else if (orientation === "horizontal") deltaX = 0;

    newVertex0X += deltaX;
    newVertex0Y += deltaY;
    newVertex1X += deltaX;
    newVertex1Y += deltaY;

    if (orientation === "vertical") {
      newVertex0Y = draggingSupport.get("startVertex0Y");
      newVertex1Y = draggingSupport.get("startVertex1Y");
    } else if (orientation === "horizontal") {
      newVertex0X = draggingSupport.get("startVertex0X");
      newVertex1X = draggingSupport.get("startVertex1X");
    }

    // Reject snaps that land this wall on another parallel wall (hides top/side).
    if (
      (deltaX !== 0 || deltaY !== 0) &&
      this.wouldCoincideWithParallelWall(
        state,
        layerID,
        lineID,
        newVertex0X,
        newVertex0Y,
        newVertex1X,
        newVertex1Y
      )
    ) {
      newVertex0X -= deltaX;
      newVertex0Y -= deltaY;
      newVertex1X -= deltaX;
      newVertex1Y -= deltaY;
      if (orientation === "vertical") {
        newVertex0Y = draggingSupport.get("startVertex0Y");
        newVertex1Y = draggingSupport.get("startVertex1Y");
      } else if (orientation === "horizontal") {
        newVertex0X = draggingSupport.get("startVertex0X");
        newVertex1X = draggingSupport.get("startVertex1X");
      }
      activeSnapElement = null;
    }

    const sharedVertices = draggingSupport.get("sharedVertices") || [];
    const anchorClones = draggingSupport.get("anchorClones") || [];
    if (sharedVertices.length > 0) {
      sharedVertices.forEach((sharedVertexID) => {
        const sharedVertex = vertices.get(sharedVertexID);
        if (!sharedVertex) return;

        const sharedLines = sharedVertex.lines || new List();
        const hasSplitBridge = sharedLines.some((connectedLineID) => {
          const connectedLine = state.getIn([
            "scene",
            "layers",
            layerID,
            "lines",
            connectedLineID,
          ]);
          return connectedLine && connectedLine.getIn(["misc", "_splitBridge"]);
        });
        if (hasSplitBridge) return;

        let cloneVertexID = null;
        const mapped = anchorClones.find(
          (entry) =>
            (entry.get && entry.get("anchorId") === sharedVertexID) ||
            entry.anchorId === sharedVertexID
        );
        if (mapped) {
          cloneVertexID =
            mapped.get && mapped.get("cloneId")
              ? mapped.get("cloneId")
              : mapped.cloneId;
        }

        const lineStillUsesShared = state
          .getIn(["scene", "layers", layerID, "lines", lineID, "vertices"])
          .includes(sharedVertexID);

        if (!cloneVertexID && lineStillUsesShared) {
          let vertex = new VertexModel({
            id: IDBroker.acquireID(),
            name: "Vertex",
            x: sharedVertex.x,
            y: sharedVertex.y,
            lines: new List([lineID]),
            areas: new List(),
          });

          state = state.setIn(
            ["scene", "layers", layerID, "vertices", vertex.id],
            vertex
          );
          cloneVertexID = vertex.id;

          const lineVertexIds = state.getIn([
            "scene",
            "layers",
            layerID,
            "lines",
            lineID,
            "vertices",
          ]);
          const sharedIndex = lineVertexIds.indexOf(sharedVertexID);
          if (sharedIndex !== -1) {
            state = state.setIn(
              [
                "scene",
                "layers",
                layerID,
                "lines",
                lineID,
                "vertices",
                sharedIndex,
              ],
              vertex.id
            );
            state = state.setIn(
              ["scene", "layers", layerID, "vertices", vertex.id, "lines"],
              new List([lineID])
            );
            state = Vertex.removeElement(
              state,
              layerID,
              sharedVertexID,
              "lines",
              lineID
            ).updatedState;
          }
        }

        if (!cloneVertexID) return;

        const lineType = line.get("type") || line.type || "wall";
        let newLine = state.catalog.factoryElement(
          lineType,
          {
            id: IDBroker.acquireID(),
            name: NameGenerator.generateName(
              "lines",
              state.catalog.getIn(["elements", lineType, "info", "title"])
            ),
            vertices: new List([sharedVertexID, cloneVertexID]),
            type: lineType,
            misc: new Map({ _splitBridge: true }),
          },
          line.get("properties")
        );
        state = state.setIn(
          ["scene", "layers", layerID, "lines", newLine.id],
          newLine
        );
        fraLog("SPLIT", "bridge wall created during drag", {
          bridgeId: newLine.id,
          from: sharedVertexID,
          to: cloneVertexID,
          thickness: newLine.getIn(["properties", "thickness"]),
        });

        // Safe attach — never crash if clone vertex/lines list is missing mid-drag.
        const cloneLines =
          state.getIn([
            "scene",
            "layers",
            layerID,
            "vertices",
            cloneVertexID,
            "lines",
          ]) || new List();
        state = state.setIn(
          ["scene", "layers", layerID, "vertices", cloneVertexID, "lines"],
          cloneLines.includes(newLine.id)
            ? cloneLines
            : cloneLines.push(newLine.id)
        );
        state = Vertex.addElement(
          state,
          layerID,
          sharedVertexID,
          "lines",
          newLine.id
        ).updatedState;
      });

      state = state.updateIn(["draggingSupport", "sharedVertices"], () => []);
      state = state.updateIn(["draggingSupport", "anchorClones"], () => []);
    }

    state = state.merge({
      activeSnapElement,
      scene: state.scene.updateIn(["layers", layerID], (layer) =>
        layer.withMutations((layer) => {
          let lineVertices = layer.getIn(["lines", lineID, "vertices"]);
          layer.updateIn(["vertices", lineVertices.get(0)], (vertex) =>
            vertex.merge({ x: newVertex0X, y: newVertex0Y })
          );
          layer.updateIn(["vertices", lineVertices.get(1)], (vertex) =>
            vertex.merge({ x: newVertex1X, y: newVertex1Y })
          );
          return layer;
        })
      ),
    });

    return { updatedState: state };
  }

  static endDraggingLine(state, x, y) {
    let { draggingSupport } = state;
    if (!draggingSupport) {
      return {
        updatedState: state.merge({
          mode: MODE_IDLE,
          draggingSupport: null,
          activeSnapElement: null,
          snapElements: new List(),
        }),
      };
    }

    let layerID = draggingSupport.get("layerID");
    let lineID = draggingSupport.get("lineID");
    const dragOrient = draggingSupport.get("lineOrientation");
    const oldAxisCoord =
      dragOrient === "vertical"
        ? draggingSupport.get("startVertex0X")
        : dragOrient === "horizontal"
          ? draggingSupport.get("startVertex0Y")
          : null;
    const sharedAtBegin = draggingSupport.get("sharedVertices") || new List();
    const clonesAtBegin = draggingSupport.get("anchorClones") || new List();
    // Prefer sticky flag — sharedVertices is cleared mid-drag by updateDraggingLine.
    let isSplitDrag =
      draggingSupport.get("isSplitDrag") === true ||
      (sharedAtBegin.size || sharedAtBegin.length || 0) > 0 ||
      (clonesAtBegin.size || clonesAtBegin.length || 0) > 0;
    if (!isSplitDrag && lineID) {
      const dragLine = state.getIn(["scene", "layers", layerID, "lines", lineID]);
      if (dragLine) {
        state.getIn(["scene", "layers", layerID, "lines"]).forEach((ln) => {
          if (!ln.getIn(["misc", "_splitBridge"])) return;
          if (dragLine.vertices.some((vid) => ln.vertices.includes(vid))) {
            isSplitDrag = true;
          }
        });
      }
    }
    const startPX = draggingSupport.get("startPointX");
    const startPY = draggingSupport.get("startPointY");
    const moveDist = Math.hypot(
      (x || 0) - (startPX || 0),
      (y || 0) - (startPY || 0)
    );
    const isClickOnly = moveDist < 5;

    // Click only: select the wall. Do NOTHING to geometry.
    // (Any detach/merge/cleanup on click was deleting walls / whole buildings.)
    if (isClickOnly) {
      const before = layerStats(state, layerID);
      state = state.merge({
        mode: MODE_IDLE,
        draggingSupport: null,
        activeSnapElement: null,
        snapElements: new List(),
      });
      if (lineID && state.hasIn(["scene", "layers", layerID, "lines", lineID])) {
        state = this.select(state, layerID, lineID).updatedState;
      }
      const after = layerStats(state, layerID);
      fraRemoteLog("WALL_CLICK_ONLY", {
        layerId: layerID,
        lineId: lineID,
        moveDist: Math.round(moveDist * 10) / 10,
        isSplitDrag: !!isSplitDrag,
        linesBefore: before.lines,
        linesAfter: after.lines,
        diagonalsBefore: before.diagonals,
        diagonalsAfter: after.diagonals,
        missing: before.lines !== after.lines,
        anglesLost: after.diagonals < before.diagonals,
        buildingGone: after.lines === 0 && before.lines > 0,
      });
      return { updatedState: state };
    }

    const statsBeforeDrag = layerStats(state, layerID);

    state = this.updateDraggingLine(state, x, y).updatedState;

    const updatedLine = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      lineID,
    ]);

    if (updatedLine && isSplitDrag) {
      const v0 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        updatedLine.vertices.get(0),
      ]);
      const v1 = state.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        updatedLine.vertices.get(1),
      ]);

      if (v0 && v1 && !GeometryUtils.samePoints(v0, v1)) {
        state = state.update("splittedVertices", () => new List([]));
        state = this.finalizeSplitDrag(
          state,
          layerID,
          lineID,
          draggingSupport
        ).updatedState;
        state = this.reconcileSplitBridges(state, layerID, lineID).updatedState;
        state = Layer.mergeEqualsVertices(
          state,
          layerID,
          updatedLine.vertices.get(0)
        ).updatedState;
        state = Layer.mergeEqualsVertices(
          state,
          layerID,
          updatedLine.vertices.get(1)
        ).updatedState;
        state = Layer.removeZeroLengthLines(state, layerID).updatedState;
        // Do NOT removeDuplicateLines here — it was deleting real parallel walls
        // (top wall / outer walls) after split-drag when coords briefly matched.
      }
    } else if (updatedLine) {
      // Normal wall drag: keep shared corners, just tidy endpoints.
      state = Layer.mergeEqualsVertices(
        state,
        layerID,
        updatedLine.vertices.get(0)
      ).updatedState;
      state = Layer.mergeEqualsVertices(
        state,
        layerID,
        updatedLine.vertices.get(1)
      ).updatedState;
      state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    }

    state = state.merge({
      mode: MODE_IDLE,
      draggingSupport: null,
      activeSnapElement: null,
      snapElements: new List(),
    });

    state = state.update("splittedVertices", () => new List([]));
    // Only merge endpoints of the dragged wall — global merge collapsed bay
    // angles / corners ("angles missing after moving walls").
    if (updatedLine) {
      state = Layer.mergeEqualsVertices(
        state,
        layerID,
        updatedLine.vertices.get(0)
      ).updatedState;
      state = Layer.mergeEqualsVertices(
        state,
        layerID,
        updatedLine.vertices.get(1)
      ).updatedState;
    }
    state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    // Never removeDuplicateLines on drag end — deletes real walls when snap/merge
    // lands two segments on the same axis (client: walls hidden after move).

    // Fix translate skew + reopen rooms: straighten near-ortho, snap free ends.
    if (lineID && state.hasIn(["scene", "layers", layerID, "lines", lineID])) {
      state = this.straightenLineIfNearOrtho(state, layerID, lineID, 12);
      if (!isSplitDrag) {
        state = this.snapLineEndsToNearbyVertices(state, layerID, lineID, 40);
      }
      state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    }
    // Heal disconnected free ends onto nearby walls (big split / transparent gap).
    if (!isSplitDrag) {
      state = this.healFreeEndsOnLayer(state, layerID, 140);
    }

    // Always refresh rooms — skip weld on normal drag (never auto-split).
    state = Layer.detectAndUpdateAreas(state, layerID, {
      skipWeld: !isSplitDrag,
    }).updatedState;
    state = Layer.ensureSolidRoomFill(state, layerID).updatedState;

    if (isSplitDrag && dragOrient && lineID && moveDist >= 15) {
      state = this.snapDraggedJunctionsOrthogonal(
        state,
        layerID,
        lineID,
        dragOrient
      );
      // NEVER repairDiagonalWalls globally — that turns bay / inverted bay
      // angles into straight walls when any split-drag finishes.
      state = this.promoteSplitBridgesOnly(state, layerID, lineID);
      state = Layer.removeZeroLengthLines(state, layerID).updatedState;
      // One careful spur pass only — repeated passes were deleting real walls.
      state = this.removeOrphanExtrusionSpurs(
        state,
        layerID,
        lineID,
        dragOrient,
        oldAxisCoord
      );
      state = Layer.removeZeroLengthLines(state, layerID).updatedState;
      // After split-drag, heal any accidental free-end gaps.
      state = this.healFreeEndsOnLayer(state, layerID, 140);
      state = Layer.detectAndUpdateAreas(state, layerID, {
        skipWeld: true,
      }).updatedState;
      state = Layer.ensureSolidRoomFill(state, layerID).updatedState;
    } else if (isSplitDrag && lineID) {
      // Tiny split-drag: keep bridges as walls, no destructive cleanup.
      state = this.promoteSplitBridgesOnly(state, layerID, lineID);
      state = Layer.removeZeroLengthLines(state, layerID).updatedState;
      state = this.healFreeEndsOnLayer(state, layerID, 140);
      state = Layer.detectAndUpdateAreas(state, layerID, {
        skipWeld: true,
      }).updatedState;
      state = Layer.ensureSolidRoomFill(state, layerID).updatedState;
    }
    state = state.update("splittedVertices", () => new List([]));

    if (lineID && state.hasIn(["scene", "layers", layerID, "lines", lineID])) {
      state = this.select(state, layerID, lineID).updatedState;
    }

    fraSplitSnapshot(state, layerID, "split drag end", {
      lineID,
      isSplitDrag,
      moveDist,
    });

    const statsAfterDrag = layerStats(state, layerID);
    fraRemoteLog("WALL_DRAG_END", {
      layerId: layerID,
      lineId: lineID,
      isSplitDrag: !!isSplitDrag,
      moveDist: Math.round(moveDist),
      linesBefore: statsBeforeDrag.lines,
      linesAfter: statsAfterDrag.lines,
      areasBefore: statsBeforeDrag.areas,
      areasAfter: statsAfterDrag.areas,
      diagonalsBefore: statsBeforeDrag.diagonals,
      diagonalsAfter: statsAfterDrag.diagonals,
      missing: statsBeforeDrag.lines !== statsAfterDrag.lines,
      anglesLost: statsAfterDrag.diagonals < statsBeforeDrag.diagonals,
      buildingGone: statsAfterDrag.lines === 0 && statsBeforeDrag.lines > 0,
    });

    return { updatedState: state };
  }

  // Clear _splitBridge flags on bridges that touch the dragged wall only.
  // Does not move vertices — preserves intentional angled walls elsewhere.
  static promoteSplitBridgesOnly(state, layerID, draggedLineID) {
    const dragged = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      draggedLineID,
    ]);
    if (!dragged) return state;
    state.getIn(["scene", "layers", layerID, "lines"]).forEach((line, id) => {
      if (!line.getIn(["misc", "_splitBridge"])) return;
      const touches = dragged.vertices.some((vid) => line.vertices.includes(vid));
      if (!touches) return;
      state = state.setIn(
        ["scene", "layers", layerID, "lines", id, "misc"],
        new Map()
      );
    });
    return state;
  }

  static setProperties(state, layerID, lineID, properties) {
    state = state.mergeIn(
      ["scene", "layers", layerID, "lines", lineID, "properties"],
      properties
    );

    return { updatedState: state };
  }

  static setJsProperties(state, layerID, lineID, properties) {
    return this.setProperties(state, layerID, lineID, fromJS(properties));
  }

  static updateProperties(state, layerID, lineID, properties) {
    properties.forEach((v, k) => {
      if (
        state.hasIn([
          "scene",
          "layers",
          layerID,
          "lines",
          lineID,
          "properties",
          k,
        ])
      )
        state = state.mergeIn(
          ["scene", "layers", layerID, "lines", lineID, "properties", k],
          v
        );
    });

    return { updatedState: state };
  }

  static updateJsProperties(state, layerID, lineID, properties) {
    return this.updateProperties(state, layerID, lineID, fromJS(properties));
  }

  static setAttributes(state, layerID, lineID, lineAttributes) {
    let lAttr = lineAttributes.toJS();
    let { vertexOne, vertexTwo, lineLength } = lAttr;

    delete lAttr["vertexOne"];
    delete lAttr["vertexTwo"];
    delete lAttr["lineLength"];

    state = state
      .mergeIn(["scene", "layers", layerID, "lines", lineID], fromJS(lAttr))
      .mergeIn(["scene", "layers", layerID, "vertices", vertexOne.id], {
        x: vertexOne.x,
        y: vertexOne.y,
      })
      .mergeIn(["scene", "layers", layerID, "vertices", vertexTwo.id], {
        x: vertexTwo.x,
        y: vertexTwo.y,
      })
      .mergeIn(
        ["scene", "layers", layerID, "lines", lineID, "misc"],
        new Map({ _unitLength: lineLength._unit })
      );

    state = Layer.mergeEqualsVertices(
      state,
      layerID,
      vertexOne.id
    ).updatedState;

    if (vertexOne.x != vertexTwo.x && vertexOne.y != vertexTwo.y) {
      state = Layer.mergeEqualsVertices(
        state,
        layerID,
        vertexTwo.id
      ).updatedState;
    }

    state = Layer.detectAndUpdateAreas(state, layerID).updatedState;

    return { updatedState: state };
  }

  static setVerticesCoords(state, layerID, lineID, x1, y1, x2, y2) {
    let line = state.getIn(["scene", "layers", layerID, "lines", lineID]);
    state = Vertex.setAttributes(
      state,
      layerID,
      line.vertices.get(0),
      new Map({ x: x1, y: y1 })
    ).updatedState;
    state = Vertex.setAttributes(
      state,
      layerID,
      line.vertices.get(1),
      new Map({ x: x2, y: y2 })
    ).updatedState;

    return { updatedState: state };
  }

  static createSquare(state, layerID, x, y, sideLength, properties) {
    let foundSnap = false;
    let snapElements = SnapSceneUtils.sceneSnapElements(
      state.scene,
      new List(),
      state.snapMask
    );
    let snap = null;
    if (state.snapMask && !state.snapMask.isEmpty()) {
      snap = SnapUtils.nearestSnap(snapElements, x, y, state.snapMask);

      if (snap) {
        let { x: snapX, y: snapY } = snap.point;
        x = snapX;
        y = snapY;
        foundSnap = true;
      }

      snapElements = snapElements.withMutations((snapElements) => {
        let a, b, c;
        ({ a, b, c } = GeometryUtils.horizontalLine(y));
        SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
        ({ a, b, c } = GeometryUtils.verticalLine(x));
        SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
      });
    }
    const points = [
      { x, y },
      { x: x + sideLength, y },
      { x: x + sideLength, y: y - sideLength },
      { x, y: y - sideLength },
    ];
    const lines = [];

    for (let i = 0; i < points.length; i++) {
      const startPoint = points[i];
      const endPoint = points[(i + 1) % points.length];

      const { updatedState, line } = Line.create(
        state,
        layerID,
        "wall",
        startPoint.x,
        startPoint.y,
        endPoint.x,
        endPoint.y,
        properties
      );

      state = updatedState;
      lines.push(line);
    }

    const tlId = lines[0].vertices.get(0);
    const trId = lines[0].vertices.get(1);
    const brId = lines[1].vertices.get(1);
    const blId = lines[2].vertices.get(1);

    const draggingSupport = Map({
      layerID,
      squareLines: lines,
      cornerVertexIDs: new List([tlId, trId, brId, blId]),
      startPointX: x,
      startPointY: y,
    });
    // let snapElements = SnapSceneUtils.sceneSnapElements(
    //   state.scene,
    //   new List(),
    //   state.snapMask
    // );
    // let snap = null;

    // if (state.snapMask && !state.snapMask.isEmpty()) {
    //   snap = SnapUtils.nearestSnap(snapElements, x, y, state.snapMask);
    //   if (snap) ({ x, y } = snap.point);

    //   snapElements = snapElements.withMutations((snapElements) => {
    //     let a, b, c;
    //     ({ a, b, c } = GeometryUtils.horizontalLine(y));
    //     SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
    //     ({ a, b, c } = GeometryUtils.verticalLine(x));
    //     SnapUtils.addLineSnap(snapElements, a, b, c, 10, 3, null);
    //   });
    // }
    // Room area is finalized when the user releases the mouse (endDrawingSquare).
    state = state
      .set("mode", MODE_DRAWING_SQUARE)
      .set("draggingSupport", draggingSupport)
      .set("snapElements", snapElements)
      .set("foundSnap", foundSnap);

    fraLog("ROOM", "createSquare — drag started (4 walls placed)", {
      layerID,
      startX: x,
      startY: y,
      cornerIds: [tlId, trId, brId, blId],
    });

    return { updatedState: state };
  }

  static updateDraggingSquare(state, x, y) {
    const draggingSupport = state.get("draggingSupport");
    if (!draggingSupport) return { updatedState: state };

    const snapElements = state.get("snapElements");
    const snapMask = state.get("snapMask");

    if (snapMask && !snapMask.isEmpty()) {
      const snap = SnapUtils.nearestSnap(snapElements, x, y, snapMask);
      if (snap) {
        x = snap.point.x;
        y = snap.point.y;
      }
    }

    const layerID = draggingSupport.get("layerID");
    const cornerVertexIDs = draggingSupport.get("cornerVertexIDs");
    const startPointX = draggingSupport.get("startPointX");
    const startPointY = draggingSupport.get("startPointY");

    if (!cornerVertexIDs || cornerVertexIDs.size !== 4) {
      return { updatedState: state };
    }

    const [tlId, trId, brId, blId] = cornerVertexIDs.toArray();

    state = Vertex.setAttributes(
      state,
      layerID,
      tlId,
      new Map({ x: startPointX, y: startPointY })
    ).updatedState;
    state = Vertex.setAttributes(
      state,
      layerID,
      trId,
      new Map({ x, y: startPointY })
    ).updatedState;
    state = Vertex.setAttributes(
      state,
      layerID,
      brId,
      new Map({ x, y })
    ).updatedState;
    state = Vertex.setAttributes(
      state,
      layerID,
      blId,
      new Map({ x: startPointX, y })
    ).updatedState;

    return { updatedState: state };
  }

  static endDraggingSquare(state, layerID) {
    state = state.merge({
      mode: MODE_IDLE,
      draggingSupport: null,
    });

    return { updatedState: state };
  }
}

export { Line as default };
